import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MagnifyingGlass, Sparkle, FloppyDisk, ArrowRight, Fire } from "@phosphor-icons/react";
import { searchWebRecipes, saveSearchedRecipe, DIET_TAG_META } from "../lib/api";

const SUGGESTIONS = [
  "high-protein vegan breakfast",
  "keto Indian dinner",
  "gluten-free Mediterranean bowl",
  "low-sodium Japanese lunch",
  "iron-rich post-workout meal",
  "kid-friendly high-calcium snack",
];

function fmt(v) { const n = Number(v || 0); return Math.abs(n) >= 100 ? n.toFixed(0) : n.toFixed(1); }

export default function WebSearch() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [savingIdx, setSavingIdx] = useState(-1);

  const runSearch = async (query) => {
    const useQuery = query || q;
    if (!useQuery.trim()) { toast.error("Enter a query"); return; }
    setQ(useQuery); setLoading(true); setResults([]);
    try {
      const d = await searchWebRecipes(useQuery, 3);
      setResults(d.recipes || []);
      if (!(d.recipes || []).length) toast.error("No recipes returned — try a different query");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally { setLoading(false); }
  };

  const save = async (idx) => {
    const r = results[idx];
    if (!r) return;
    setSavingIdx(idx);
    try {
      const rec = await saveSearchedRecipe({
        name: r.name, servings: r.servings,
        ingredients: r.ingredients.map(i => ({ name: i.name, quantity: i.quantity, unit: i.unit })),
        cuisine: r.cuisine, why_matches: r.why_matches,
      });
      toast.success("Saved to library");
      nav(`/recipe/${rec.id}`);
    } catch (e) { toast.error("Save failed"); }
    finally { setSavingIdx(-1); }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-8 space-y-6" data-testid="search-page">
      <div className="pb-6 border-b border-border">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Recipe research</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-2">Search the web</h1>
        <p className="mt-3 text-muted-foreground max-w-2xl">Scan blogs, cookbooks, articles, audios and videos with Gemini 3.1 Pro. Enter any criteria — cuisine, macros, dietary rules, occasion — and get 5 candidate recipes with full nutrient profiles computed in place.</p>
      </div>

      <div className="border border-border flex items-stretch" data-testid="search-bar">
        <div className="w-11 flex items-center justify-center border-r border-border">
          <MagnifyingGlass size={16} className="text-muted-foreground" />
        </div>
        <input
          value={q} onChange={e => setQ(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") runSearch(); }}
          data-testid="search-query"
          placeholder='e.g. "high-protein vegan breakfast under 500 kcal"'
          className="flex-1 h-14 px-4 bg-transparent focus:outline-none font-mono text-sm"
        />
        <button onClick={() => runSearch()} disabled={loading} data-testid="search-btn"
          className="h-14 px-6 border-l border-border bg-primary text-primary-foreground font-mono text-xs uppercase tracking-[0.15em] hover:bg-primary/90 disabled:opacity-60 flex items-center gap-2 transition-colors duration-200">
          {loading ? <><Sparkle size={14} className="animate-pulse" /> Searching…</> : <><Sparkle size={14} /> Research</>}
        </button>
      </div>

      {!results.length && !loading && (
        <div className="border border-border p-6" data-testid="suggestions">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">Try one of these</div>
          <div className="flex flex-wrap gap-2">
            {SUGGESTIONS.map(s => (
              <button key={s} onClick={() => runSearch(s)} data-testid={`suggestion-${s.replace(/\s+/g, "-")}`}
                className="h-9 px-3 border border-border hover:bg-muted font-mono text-xs transition-colors duration-200">
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {loading && (
        <div className="border border-border p-12 text-center" data-testid="search-loading">
          <Sparkle size={24} className="mx-auto animate-pulse text-primary" />
          <div className="mt-3 font-display font-bold text-lg">Analyzing sources…</div>
          <div className="mt-1 text-sm text-muted-foreground">Gemini is scanning cookbooks, blogs and channels — this takes ~10-20 s</div>
        </div>
      )}

      {results.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-border border border-border" data-testid="search-results">
          {results.map((r, i) => (
            <div key={`${r.name}-${i}`} className="bg-card p-6 space-y-4" data-testid={`result-${i}`}>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                  {r.cuisine || "Recipe"} · {r.servings} serving{r.servings > 1 ? "s" : ""}
                </div>
                <h3 className="font-display font-bold text-2xl tracking-tight mt-1">{r.name}</h3>
                {r.why_matches && (
                  <p className="text-sm text-muted-foreground mt-1 leading-snug">{r.why_matches}</p>
                )}
              </div>

              <div className="grid grid-cols-4 gap-px bg-border border border-border">
                {[
                  { k: "calories", label: "kcal" },
                  { k: "protein", label: "P" },
                  { k: "carbs", label: "C" },
                  { k: "fat", label: "F" },
                ].map(m => (
                  <div key={m.k} className="bg-card p-2 text-center">
                    <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">{m.label}</div>
                    <div className="font-mono text-base font-medium">{fmt(r.per_serving?.[m.k])}</div>
                  </div>
                ))}
              </div>

              {(r.diet_tags || []).length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {r.diet_tags.slice(0, 6).map(t => {
                    const meta = DIET_TAG_META[t] || { label: t, color: "#888" };
                    return (
                      <span key={t} className="font-mono text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 border"
                        style={{ borderColor: meta.color, color: meta.color }}>{meta.label}</span>
                    );
                  })}
                </div>
              )}

              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1.5">Ingredients ({r.ingredients.length})</div>
                <ul className="space-y-0.5 max-h-32 overflow-y-auto font-mono text-xs">
                  {r.ingredients.slice(0, 12).map((ing, k) => (
                    <li key={`${ing.name}-${k}`} className="flex justify-between border-b border-border py-1 last:border-b-0">
                      <span className="capitalize">{ing.name}</span>
                      <span className="text-muted-foreground">{fmt(ing.quantity)} {ing.unit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <button onClick={() => save(i)} disabled={savingIdx === i} data-testid={`save-result-${i}`}
                className="w-full h-11 bg-foreground text-background font-mono text-xs uppercase tracking-[0.15em] hover:bg-foreground/90 disabled:opacity-60 flex items-center justify-center gap-2 transition-colors duration-200">
                {savingIdx === i ? <><Sparkle size={14} className="animate-pulse" /> Saving…</> : <><FloppyDisk size={14} /> Save & Open <ArrowRight size={12} /></>}
              </button>
            </div>
          ))}
        </div>
      )}

      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
        Results are AI-generated using Gemini 3.1 Pro and cross-referenced with our nutrient databases. Always verify serving sizes for medical use.
      </p>
    </div>
  );
}
